import classnames from 'classnames'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useApolloClient, useQuery } from '@apollo/client'
import { Pagination, Tooltip } from 'antd'
import orderDetailsQuery from '@Orders/OrderDetailsPage/Queries/orderDetailsQuery'
import { useGetOboDetails } from '@Admin/AdminQuotingPage/components/PartDFMReportPanel/logic'
import { IM_DFM_URL_PARAM } from '@Builder/QuoteDetailPage/FilesTab/ImQuoteTabSections/constants'
import { MANUFACTURABILITY_FEEDBACK_TAB } from '@Orders/constants'

import Box from '~/core/Box'
import {
    ADMIN_PATH,
    ORDERS_PATH,
    QUOTES_PATH,
} from '~/redux-modules/preferences'
import logger from '~/lib/logger'

import NavigateArrowIcon, {
    NEXT_NAVIGATE,
    PREV_NAVIGATE,
} from './NavigateArrowIcon'
import NavigateJumpIcon, {
    JUMP_NEXT_NAVIGATE,
    JUMP_PREV_NAVIGATE,
} from './NavigateJumpIcon'
import qmrsInfoFilteredByHasDfmQuery from './qmrsInfoFilteredByHasDfmQuery'
import salesOrderQuery from './salesOrderQuery'
import { qmrsInfoFilteredByHasDfm_qmrsInfoFilteredByHasDfm as QmrInfoType } from './__generated__/qmrsInfoFilteredByHasDfm'
import styles from './styles.css'

const PAGINATION_LIMIT = 1

const PAGE_NAVIGATE = 'page'

type ParamsType = {
    id?: string
    orderName?: string
    qmrId?: string
    quoteId?: string
    userId?: string
}

const QmrNavigation = () => {
    const client = useApolloClient()
    const navigate = useNavigate()

    const {
        id: builderQuoteId,
        orderName,
        qmrId,
        quoteId: oboQuoteId,
        userId,
    } = useParams<ParamsType>()

    const { isObo } = useGetOboDetails(userId || '')

    const orderDetailsCacheData = client.readQuery({
        query: orderDetailsQuery,
        variables: { name: orderName },
    })

    const cachedOrderId = orderDetailsCacheData?.salesOrder?.id

    const { data: salesOrderData } = useQuery(salesOrderQuery, {
        variables: { name: orderName },
        skip: Boolean(cachedOrderId),
    })

    const queriedOrderId = salesOrderData?.salesOrder?.id
    const orderId = cachedOrderId || queriedOrderId

    const variables = orderId
        ? { orderId }
        : {
              quoteId: builderQuoteId || oboQuoteId,
          }

    const { data, error } = useQuery(qmrsInfoFilteredByHasDfmQuery, {
        variables,
    })

    const qmrsInfo = data?.qmrsInfoFilteredByHasDfm
    const qmrsInfoTotalCount = qmrsInfo?.length || 1

    const activeQmrIdx = qmrsInfo?.findIndex(
        (qmr: QmrInfoType) => qmr?.id === qmrId,
    )

    const [pageNumber, setPageNumber] = useState(activeQmrIdx + 1)
    const [qmrs, setQmrs] = useState(qmrsInfo)
    const [qmrsTotalCount, setQmrsTotalCount] = useState(qmrsInfoTotalCount)

    useEffect(() => {
        if (qmrsInfo) {
            setPageNumber(activeQmrIdx + 1)
            setQmrs(qmrsInfo)
            setQmrsTotalCount(qmrsInfoTotalCount)
        }
    }, [activeQmrIdx, qmrsInfo, qmrsInfoTotalCount])

    const onChangePage = (upcomingPageNumber: number) => {
        setPageNumber(upcomingPageNumber)

        const selectedQmrId = qmrs[upcomingPageNumber - 1]?.id

        let builderPath
        let oboPath

        if (oboQuoteId || builderQuoteId) {
            builderPath = `${QUOTES_PATH}/${builderQuoteId}/qmr/${selectedQmrId}/${IM_DFM_URL_PARAM}`
            oboPath = `${ADMIN_PATH}/user/${userId}/quotes/${oboQuoteId}/qmr/${selectedQmrId}/${IM_DFM_URL_PARAM}`
        } else if (orderName) {
            builderPath = `${ORDERS_PATH}/${orderName}/qmr/${selectedQmrId}/${IM_DFM_URL_PARAM}#${MANUFACTURABILITY_FEEDBACK_TAB}`
            oboPath = `${ADMIN_PATH}/user/${userId}${ORDERS_PATH}/${orderName}/qmr/${selectedQmrId}/${IM_DFM_URL_PARAM}#${MANUFACTURABILITY_FEEDBACK_TAB}`
        }

        navigate(
            {
                pathname: isObo ? oboPath : builderPath,
            },
            { replace: true },
        )
    }

    if (error) {
        logger.exception(error, {
            info: 'QmrNavigation::Failed to fetch qmrs for qmrsInfoFilteredByHasDfm query',
            builderQuoteId,
            oboQuoteId,
            orderId,
            userId,
        })

        return null
    }

    const setItemRender = (page: number, type: string) => {
        if (type === PAGE_NAVIGATE) {
            const qmr = qmrs?.[page - 1]

            const fileName = qmr?.fileName
            const thumbnailUri = qmr?.thumbnailUri

            return (
                <Tooltip title={fileName}>
                    <img
                        className={classnames(styles.partThumbnail)}
                        src={thumbnailUri}
                    />
                </Tooltip>
            )
        }

        if (type === PREV_NAVIGATE) {
            return (
                <NavigateArrowIcon disabled={activeQmrIdx === 0} type={type} />
            )
        }
        if (type === NEXT_NAVIGATE) {
            return (
                <NavigateArrowIcon
                    disabled={activeQmrIdx === qmrsTotalCount - 1}
                    type={type}
                />
            )
        }

        if (type === JUMP_PREV_NAVIGATE) {
            return <NavigateJumpIcon type={type} />
        }

        if (type === JUMP_NEXT_NAVIGATE) {
            return <NavigateJumpIcon type={type} />
        }

        return null
    }

    return (
        <Box className={styles.modalPaginationWrapper}>
            <Pagination
                current={pageNumber}
                defaultCurrent={pageNumber}
                defaultPageSize={PAGINATION_LIMIT}
                hideOnSinglePage={true}
                itemRender={setItemRender}
                onChange={onChangePage}
                showLessItems
                showSizeChanger={false}
                style={{
                    margin: '20px 0',
                    textAlign: 'center',
                }}
                total={qmrsTotalCount}
            />
        </Box>
    )
}

export default QmrNavigation
